import React, { JSX } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Build, CheckCircle, Description, Psychology, Star, Work } from '@mui/icons-material';
import * as Types from 'types/types';

function RequirementSection(props: {
  title: string;
  items: string[] | undefined;
  icon: JSX.Element;
  required?: boolean;
}): JSX.Element {
  if (!props.items || props.items.length === 0) return <></>;

  return (
    <Box sx={{ mb: 2, display: 'flex', position: 'relative', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        {props.icon}
        <Typography
          variant="subtitle1"
          sx={{ ml: 1, fontWeight: 600, fontSize: '0.85rem !important' }}
        >
          {props.title}
        </Typography>
        {props.required && (
          <Chip
            label="Required"
            size="small"
            color="error"
            sx={{ ml: 1, fontSize: '0.75rem !important' }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, position: 'relative' }}>
        {props.items.map(item => (
          <Box
            key={item}
            sx={{
              border: '1px solid grey',
              p: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem !important',
              display: 'flex',
            }}
          >
            {item}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function JobRequirements(props: { activeJob: Types.Job }): JSX.Element {
  const { activeJob } = props;
  if (!activeJob.requirements) return <></>;

  return (
    <Box
      sx={{
        maxWidth: '100%',
        position: 'relative',
        m: 0,
        p: 0,
        background: 'transparent !important',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{ p: 0, pb: 1, alignItems: 'center', display: 'flex', flexDirection: 'row', gap: 1 }}
      >
        <CheckCircle color="success" />
        <Box>Job Requirements Analysis</Box>
      </Box>
      <Box sx={{ p: 0 }}>
        <RequirementSection
          title="Technical Skills (Required)"
          items={activeJob.requirements.technicalSkills.required}
          icon={<Build color="primary" />}
          required
        />
        <RequirementSection
          title="Technical Skills (Preferred)"
          items={activeJob.requirements.technicalSkills.preferred}
          icon={<Build color="action" />}
        />
        <RequirementSection
          title="Experience Requirements (Required)"
          items={activeJob.requirements.experienceRequirements.required}
          icon={<Work color="primary" />}
          required
        />
        <RequirementSection
          title="Experience Requirements (Preferred)"
          items={activeJob.requirements.experienceRequirements.preferred}
          icon={<Work color="action" />}
        />
        <RequirementSection
          title="Soft Skills"
          items={activeJob.requirements.softSkills}
          icon={<Psychology color="secondary" />}
        />
        <RequirementSection
          title="Experience"
          items={activeJob.requirements.experience}
          icon={<Star color="warning" />}
        />
        <RequirementSection
          title="Education"
          items={activeJob.requirements.education}
          icon={<Description color="info" />}
        />
        <RequirementSection
          title="Certifications"
          items={activeJob.requirements.certifications}
          icon={<CheckCircle color="success" />}
        />
        <RequirementSection
          title="Preferred Attributes"
          items={activeJob.requirements.preferredAttributes}
          icon={<Star color="secondary" />}
        />
      </Box>
    </Box>
  );
}
